import axios from "axios";
import { API_BASE_URL } from "configs/AppConfig";
import { signOutSuccess } from "store/slices/authSlice";
import store from "../store";
import { AUTH_TOKEN, AUTH_SESSION_EXPIRY } from "constants/AuthConstant";
import { notification } from "antd";

const service = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
});

// Config
const TOKEN_PAYLOAD_KEY = "Authorization";

const clearLocalAuth = () => {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(AUTH_SESSION_EXPIRY);
};

const signOutClientSide = () => {
  clearLocalAuth();
  store.dispatch(signOutSuccess());
};

let isHandling401 = false;

let sessionExpiryTimeout = null;

const disarmSessionExpiryTimer = () => {
  if (sessionExpiryTimeout) {
    clearTimeout(sessionExpiryTimeout);
    sessionExpiryTimeout = null;
  }
};

const armSessionExpiryTimer = (expiry) => {
  disarmSessionExpiryTimer();
  if (!expiry) return;
  const expiryMs = Number(expiry);
  if (!Number.isFinite(expiryMs)) return;
  const remaining = expiryMs - Date.now();
  const fireExpiry = () => {
    notification.error({
      message: "Session expired",
      description: "Please login again.",
    });
    signOutClientSide();
  };
  if (remaining <= 0) {
    fireExpiry();
    return;
  }
  sessionExpiryTimeout = setTimeout(fireExpiry, remaining);
};

let prevAuthToken = null;
let prevSessionExpiry = null;
setTimeout(() => {
  const initial = store.getState()?.auth;
  prevAuthToken = initial?.token || null;
  prevSessionExpiry = initial?.sessionExpiry || null;
  if (prevAuthToken && prevSessionExpiry) {
    armSessionExpiryTimer(prevSessionExpiry);
  }
  store.subscribe(() => {
    const next = store.getState()?.auth;
    const t = next?.token || null;
    const e = next?.sessionExpiry || null;

    if (t && !prevAuthToken) {
      isHandling401 = false;
    }
    if (!t && prevAuthToken) {
      disarmSessionExpiryTimer();
    }
    if (t && e && e !== prevSessionExpiry) {
      armSessionExpiryTimer(e);
    }
    prevAuthToken = t;
    prevSessionExpiry = e;
  });
}, 0);

// API Request interceptor
service.interceptors.request.use(
  (config) => {
    const jwtToken = localStorage.getItem(AUTH_TOKEN) || null;

    if (jwtToken) {
      config.headers[TOKEN_PAYLOAD_KEY] = `Bearer ${jwtToken}`;
    }

    return config;
  },
  (error) => {
    notification.error({
      message: "Error",
    });
    return Promise.reject(error);
  }
);

service.interceptors.response.use(
  (response) => {
    return response?.data;
  },
  (error) => {
    let notificationParam = {
      message: "Something went wrong!",
    };

    const status = error.response?.status;
    const data = error.response?.data;

    if (status === 401 || status === 403) {
      if (isHandling401) return Promise.reject(error);
      isHandling401 = true;
      notification.error({
        message: "Authentication Failed",
        description: "Please login again.",
      });
      signOutClientSide();
      return Promise.reject(error);
    }

    // Handle specific errors
    if ([400, 409, 404, 422, 500].includes(status)) {
      // For validation error structure like { errors: { field: ['msg'] } }
      if (data?.errors && typeof data.errors === "object") {
        const validationMessages = Object.values(data.errors).flat().join(", ");
        notificationParam.message = "Validation Error";
        notificationParam.description = validationMessages;
      } else if (data?.message) {
        // If there's a message field
        notificationParam.message = Array.isArray(data.message)
          ? data.message.join(", ")
          : data?.message;
      }
    }

    if (status === 508) {
      notificationParam.message = "Request Timeout";
    }

    // Fallback message if nothing matched
    if (!notificationParam.message && data?.message) {
      notificationParam.message = data.message;
    }

    notification.error(notificationParam);

    return Promise.reject(error);
  }
);

export default service;
